import React, { useCallback, useEffect, useReducer } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import moment from "moment-timezone";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";

import ai from "../../helpers/axios";
import { LoadingButton } from "../shared/loading-button";

momentTimezone.moment = moment;

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }

    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        ...(action.payload || {}),
      };
    }

    case "closeModal": {
      return {
        ...state,
        showModal: false,
        showDeleteModal: false,
        date: "",
      };
    }

    default:
      break;
  }
  return state;
};

const ClientPayDateModule = (props) => {
  const initialState = {
    showModal: false,
    showDeleteModal: false,
    saving: false,
    date: "",
    clientPayDate: props.data || null,
    loading: false,
  };

  const { getAccessTokenSilently } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  const { setUnsavedChanges } = props;

  useEffect(() => {
    setUnsavedChanges?.({
      section: "clientPayDateModal",
      value: !!state.showModal,
    });
  }, [setUnsavedChanges, state.showModal]);

  const handleAddItem = useCallback((event) => {
    event.preventDefault();

    dispatch({
      type: "openModal",
    });
  }, []);

  const handleEditItem = useCallback((event) => {
    event.preventDefault();

    dispatch({
      type: "openModal",
      payload: {
        date: state.clientPayDate,
      },
    });
  }, [state.clientPayDate]);

  const handleDeleteItem = useCallback((event) => {
    event.preventDefault();

    dispatch({
      type: "openDeleteModal",
      payload: {
        date: state.clientPayDate,
      },
    });
  }, [state.clientPayDate]);

  const handleCloseModal = useCallback(() => {
    dispatch({
      type: "closeModal",
    });
  }, []);

  const handleSubmitItem = useCallback(async (event) => {
    event.preventDefault();

    if (!state.date) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently();
      const response = await ai.auth(token).post(
        `/api/trips/${props.tripId}/clientPayDate`,
        {
          clientPayDate: state.date,
        },
      );

      if (response) {
        handleCloseModal();

        props.parentDispatch({
          type: "fieldChanged",
          field: "refreshDataToken",
          value: Date.now(),
        });
      }
    } finally {
      dispatch({
        type: "fieldChanged",
        field: "saving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, handleCloseModal, props, state.date]);

  const handleDeleteItemConfirm = useCallback(async (event) => {
    event.preventDefault();

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    try {
      const token = await getAccessTokenSilently();
      const response = await ai.auth(token).post(
        `/api/trips/${props.tripId}/clientPayDate`,
        {
          clientPayDate: null,
        },
      );

      if (response) {
        handleCloseModal();

        props.parentDispatch({
          type: "fieldChanged",
          field: "refreshDataToken",
          value: Date.now(),
        });
      }
    } finally {
      dispatch({
        type: "fieldChanged",
        field: "saving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, handleCloseModal, props]);

  return (
    <>
      <div
        className="trip-notes-module"
        key={`key-client-pay-date-${props.dataDownloadedTimestamp.toString()}`}
        id="trip-client-pay-date"
      >
        <h5 className="mb-1 d-flex">
          Client Pay Date

          {!props.readOnly && (
            <button
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              onClick={handleAddItem}
              title="Add"
              disabled={state.clientPayDate}
            >
              <span className="material-icons">add_circle</span>
            </button>
          )}
        </h5>

        <div className="trip-notes-table trip-client-pay-date-table pb-0">
          <table>
            <thead className="fm-l2-parent-table-head">
            <tr className="fm-l2-parent-table-head-row">
              <th>Date</th>
              <th className="text-align-right">Actions</th>
            </tr>
            </thead>
            <tbody className="fm-l2-parent-table-body">
            {!state.clientPayDate && (
              <tr className="fm-l2-parent-table-row">
                <td className="text-align-left" colSpan={2}>N/A</td>
              </tr>
            )}
            {state.clientPayDate && (
              <tr className="fm-l2-parent-table-row">
                <td>{moment(state.clientPayDate).format("M/D/YY")}</td>

                <td>
                  {!props.readOnly && (
                    <div className="d-flex justify-content-end">
                      <button
                        className="base-button icon-button icon-button-small evo-blue"
                        onClick={handleEditItem}
                        title="Edit"
                      >
                        <span className="material-icons">edit</span>
                      </button>

                      <button
                        className="base-button icon-button icon-button-small btn-danger ml-2"
                        onClick={handleDeleteItem}
                        title="Delete"
                      >
                        <span className="material-icons">delete</span>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            )}
            </tbody>
          </table>
        </div>
      </div>

      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "350px",
            maxHeight: "80vh",
            minWidth: "calc(min(350px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header edit-note-modal">
          <h4>{state.clientPayDate ? "Edit" : "Add"} Client Pay Date</h4>
        </div>
        <div className="modal-body edit-note-modal">
          <form onSubmit={handleSubmitItem} className="w-100 base-form mt-20">
            <div className="datepickerContainer mb-30">
              <label htmlFor={`date`} className="base-label">
                Date <span className="required">*</span>
              </label>

              <Datepicker
                value={state.date}
                onChange={(event) => {
                  dispatch({
                    type: "fieldChanged",
                    field: "date",
                    value: event.value,
                  });
                }}
                dataTimezone="utc"
                displayTimezone="local"
                timezonePlugin={momentTimezone}
                controls={["date"]}
              />
            </div>

            <div className="w-100 mt-20 d-flex gap-20">
              <LoadingButton
                className="base-button evo-blue flex-half"
                type="submit"
                disabled={state.saving}
              >
                Submit
              </LoadingButton>

              <button
                className="base-button btn-danger flex-half"
                type="button"
                onClick={handleCloseModal}
                disabled={state.saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Client Pay Date</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            Client pay date record will be permanently deleted from the trip. Are you sure you want to continue?
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseModal}
              disabled={state.saving}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              onClick={handleDeleteItemConfirm}
              isLoading={state.saving}
            >
              Delete
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default ClientPayDateModule;
